import { useEffect, useState, useCallback, Fragment } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView, EmptyState } from "@/components/StateViews";
import DateRangePicker from "@/components/DateRangePicker";

const scoreColor = (s) => (s == null ? "text-ink/40" : s >= 70 ? "text-emerald-600" : s >= 55 ? "text-amber-500" : "text-red-500");
const sevChip = (sev) => sev === "critical" ? "bg-red-50 text-red-600" : sev === "warning" ? "bg-amber-50 text-amber-600" : sev === "positive" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600";

export default function ManagerControlReport() {
  const [filters, setFilters] = useState({ created_from: "", created_to: "" });
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await minDelay(api.get("/scoring/compute-batch", { params: { entity_type: "manager", ...params } }));
      setCfg(data.data.config);
      setRows((data.data.rows || []).filter((r) => !r.insufficient));
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  if (loading && !rows) return <Loading label="Loading manager control report..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const dynTip = (b) => (b || []).map((x) => `${x.label}: ${x.available ? x.score : "no data"} (${x.weight}%)`).join("\n");

  return (
    <div className="space-y-6" data-testid="manager-report-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><ShieldCheck className="h-7 w-7 text-loop" />Manager Control</h1>
          <p className="text-sm text-ink/60">Is each manager keeping their team working leads properly and preventing leakage?{cfg && <span className="ml-1 text-ink/40">· {cfg.name} v{cfg.version}</span>}</p>
        </div>
        <DateRangePicker from={filters.created_from} to={filters.created_to} onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))} testid="mgr-date" />
      </div>

      <div className="rounded-2xl border border-sky/70 bg-white" data-testid="mgr-table">
        {!rows || !rows.length ? <EmptyState title="No managers with team data in this range." /> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-mist/70 text-left text-ink/50"><tr>
                {["Manager", "Control Score", "Diagnosis", ""].map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-sky/40">
                {rows.map((r) => (
                  <Fragment key={r.entity_id}>
                    <tr className="hover:bg-sky/10" data-testid={`mgr-row-${r.entity_id}`}>
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-navy">{r.entity_name}</td>
                      <td className={`px-3 py-2 font-bold ${scoreColor(r.score)}`} title={r.insufficient ? "Not enough team data" : dynTip(r.breakdown)} data-testid={`mgr-score-${r.entity_id}`}>
                        <span className="cursor-help">{r.insufficient || r.score == null ? "—" : r.score}</span>
                        <span className="block text-[10px] font-medium text-ink/45">{r.label}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(r.flags || []).slice(0, 4).map((f) => (
                            <span key={f.key} title={`${f.diagnosis || ""}${f.recommended_action ? "\n→ " + f.recommended_action : ""}`} className={`cursor-help rounded px-1.5 py-0.5 text-[10px] font-semibold ${sevChip(f.severity)}`}>{f.label}</span>
                          ))}
                          {!(r.flags || []).length && <span className="text-[10px] text-ink/30">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {!r.insufficient && <button onClick={() => setOpen(open === r.entity_id ? null : r.entity_id)} data-testid={`mgr-expand-${r.entity_id}`} className="text-[11px] font-semibold text-loop hover:underline">{open === r.entity_id ? "Hide" : "Breakdown"}</button>}
                      </td>
                    </tr>
                    {open === r.entity_id && (
                      <tr className="bg-mist/20"><td colSpan={4} className="px-4 py-3">
                        <div className="grid gap-2 md:grid-cols-2">
                          {(r.breakdown || []).map((b) => (
                            <div key={b.key} className="rounded-lg border border-sky/50 bg-white px-3 py-2">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-navy">{b.label}</span><span className="text-[11px] text-ink/40">{b.weight}%</span>
                                <div className="ml-auto flex items-center gap-2">
                                  {b.available ? (<><div className="h-1.5 w-20 overflow-hidden rounded-full bg-ink/10"><div className="h-full rounded-full bg-loop" style={{ width: `${b.score}%` }} /></div><span className="w-9 text-right font-semibold text-navy">{b.score}</span></>) : <span className="text-[11px] italic text-ink/40">no data</span>}
                                </div>
                              </div>
                              {b.sub && <div className="mt-1 space-y-0.5 pl-2">{b.sub.map((s) => <div key={s.key} className="flex items-center gap-2 text-[11px] text-ink/55"><span>{s.label}</span><span className="text-ink/30">{s.weight}%</span><span className="ml-auto font-semibold text-ink/60">{s.score ?? "—"}</span></div>)}</div>}
                            </div>
                          ))}
                        </div>
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
